import { BasePageLayout } from "@mlpa/core-ui-core-open_ocean_ui";
import ErrorBoundary from "globalErrorhandling/ErrorBoundary";
import { useGetCurrentUser } from "lib/getCurrentUser/useGetCurrentUser";
import LayoutOutlet from "components/templates/LayoutOutlet";
import React, { useState, useCallback, useMemo } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { useBreadcrumbs } from "./useBreadcrumbs";
import { MainLayoutTabs } from "./MainLayoutTabs";
import { MainLayoutSidebar } from "./MainLayoutSidebar";
import { MainLayoutHeader } from "./MainLayoutHeader";
import { useRecoilState } from "recoil";
import { authorizedRoutesAtom } from "authorization/state";

interface RouteMatchWithHandle {
  handle?: {
    pageTitle?: string;
    label?: string;
  };
}

export interface MainLayoutProps {
  noElevation?: boolean;
  noPadding?: boolean;
  open?: boolean;
  children?: React.ReactNode;
}

const MainLayout: React.FC<React.PropsWithChildren<MainLayoutProps>> = (props) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const { user, initials } = useGetCurrentUser();
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs();
  const matches = useMatches() as RouteMatchWithHandle[];

  const handleToggleSidebar = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  // Extrahiere nur die erste Ebene des Pfads für Sidebar (z.B. /assetManagement)
  const firstLevelPath = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : "/";
  }, [location.pathname]);

  const titleContent = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      if (matches[i]?.handle?.pageTitle) {
        return matches[i].handle!.pageTitle!;
      }
    }
    return breadcrumbs[breadcrumbs.length - 1]?.label || "";
  }, [matches, breadcrumbs]);

  const titleSlot = useMemo(() => <MainLayoutTabs title={titleContent} />, [titleContent]);
  const [authorizedRoutes] = useRecoilState(authorizedRoutesAtom);
  const sidebarSlot = useMemo(
    () => (
      <MainLayoutSidebar
        expanded={expanded}
        onToggle={handleToggleSidebar}
        userInitials={initials || "??"}
        userName={user?.displayName}
        userEmail={user?.email}
        currentPath={firstLevelPath}
        authorizedRoutes={authorizedRoutes}
      />
    ),
    [expanded, handleToggleSidebar, initials, user?.displayName, user?.email, firstLevelPath, authorizedRoutes]
  );

  const headerSlot = useMemo(() => <MainLayoutHeader />, []);

  return (
    <BasePageLayout sidebarSlot={sidebarSlot} headerSlot={headerSlot} titleSlot={titleSlot}>
      <LayoutOutlet />
      <ErrorBoundary>{props.children}</ErrorBoundary>
    </BasePageLayout>
  );
};

export default MainLayout;
